import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CRIcon, Inline } from '../../crRender'
import './style.css'

const FileUpload = ({ accept, value, setValue, label, isRequired = false }) => {
    const { t } = useTranslation()
    const inputRef = useRef(null)
    const dropZoneRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)
    const [fileName, setFileName] = useState(null)

    useEffect(() => {
        if (value) {
            setFileName(value.name)
        } else {
            setFileName(null)
            if (inputRef.current) {
                inputRef.current.value = ""
            }
        }
    }, [value])

    const handleFileSelect = (file) => {
        if (file) {
            setValue(file)
            setFileName(file.name)
        }
    }

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleInputChange = (e) => {
        const files = e.target.files
        if (files && files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleClick = () => {
        inputRef.current?.click()
    }

    const handleRemove = (e) => {
        e.stopPropagation()
        setValue(null)
        setFileName(null)
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }

    const getFileIcon = () => {
        if (fileName) {
            if (fileName.endsWith('.pdf')) return 'mdi:file-pdf-box'
            if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'mdi:file-word-box'
            if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return 'mdi:file-excel-box'
            return 'mdi:file-document'
        }
        return 'mdi:cloud-upload-outline'
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return ''
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
    }

    return (
        <div className="file-upload-container">
            {label && (
                <label className="file-upload-label">
                    {label}
                    <Inline isRender={isRequired} className="required">*</Inline>
                </label>
            )}

            <div
                ref={dropZoneRef}
                className={`file-upload-dropzone ${isDragging ? 'dragging' : ''} ${fileName ? 'has-file' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    className="file-upload-input"
                />

                {fileName ? (
                    <div className="file-upload-preview">
                        <div className="file-icon-wrapper">
                            <CRIcon icon={getFileIcon()} className="file-icon" />
                        </div>
                        <div className="file-info">
                            <div className="file-name">{fileName}</div>
                            {value?.size && (
                                <div className="file-size">{formatFileSize(value.size)}</div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="file-remove-btn"
                            onClick={handleRemove}
                            title={t("remove")}
                        >
                            <CRIcon icon="mdi:close-circle" />
                        </button>
                    </div>
                ) : (
                    <div className="file-upload-placeholder">
                        <CRIcon icon={getFileIcon()} className="upload-icon" />
                        <div className="upload-text">
                            <span className="upload-text-main">{t("drag-drop-files")}</span>
                            <span className="upload-text-sub">{t("supported-formats-pdf")}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FileUpload

